import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import StoreIcon from "@mui/icons-material/Store";
import { publishPurchaseOrders } from "../../services";
import { numberWithCommas } from "../../utils";
import {
  PURCHASE_ORDER_CHAIN_LABEL,
  PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL,
  PURCHASE_ORDER_STATUS_LABEL,
  PurchaseOrderImportConfirmData,
  PurchaseOrderImportReportRow,
  PurchaseOrderPublishData,
} from "../../types/purchase-order.type";
import { downloadUnreadableRowsCsv } from "./reportCsv";
import { buildPurchaseOrderListLink } from "../PurchaseOrder/data";
import { branchOptionLabel } from "./BranchChoiceField";
import { CARD_CONTENT_SX, CARD_SX } from "./constants";

interface DoneStepProps {
  result: PurchaseOrderImportConfirmData;
  /** แถวที่ข้าม (อ่านไม่ได้ + เกินกำหนดส่งที่ไม่ได้นำเข้า) สำหรับ CSV */
  skippedReport: PurchaseOrderImportReportRow[];
  onRestart: () => void;
}

/**
 * ขั้นที่ 3 ของ W2 — สรุปสิ่งที่บันทึกไป แล้วส่งให้สาขาต่อได้เลยจากจอเดียวกัน
 *
 * "ส่งให้สาขา" ไม่เคยปฏิเสธ ใบที่ยังไม่รู้ร้านก็ถูกส่งไปด้วย และสาขาเป็นคนจับคู่ร้าน
 * ทีหลังในจอ PO ของสาขา — ทั้งสองอย่างนี้ตรงกับ W5 ที่เปิดจากรายการ PO
 *
 * ใบที่บันทึกไว้โดยยังไม่ระบุสาขาถูกข้ามตอนส่ง (backend นับไว้ใน skipped_no_branch_count)
 * จอนี้จึงพาไปต่อที่ W1 ซึ่งกรองเฉพาะใบกลุ่มนั้นของไฟล์นี้ไว้ให้แล้ว
 */
const DoneStep = ({ result, skippedReport, onRestart }: DoneStepProps) => {
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] =
    useState<PurchaseOrderPublishData | null>(null);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const {
        data: { data },
      } = await publishPurchaseOrders({ batch_uuid: result.batch_uuid });
      // ตอบว่างถือว่ายังไม่สำเร็จ ปล่อยให้ปุ่มค้างไว้ให้กดซ้ำได้
      if (data) setPublishResult(data);
    } catch {
      // interceptor แสดง error ให้แล้ว · กดส่งซ้ำได้ ระบบส่งเฉพาะใบที่ยังไม่ได้ส่ง
    } finally {
      setPublishing(false);
    }
  };

  // ใบที่ยังไม่ระบุสาขาส่งไม่ได้ ปุ่มจึงนับเฉพาะใบที่มีสาขาแล้ว
  const publishableCount = Math.max(
    result.saved_count - result.no_branch_count,
    0,
  );
  const hasNothingToPublish = publishableCount === 0;
  const hasNoBranch = result.no_branch_count > 0;
  const noBranchListLink = buildPurchaseOrderListLink(
    result.batch_uuid,
    "NO_BRANCH",
  );
  const publishedText = publishResult
    ? `ส่งให้สาขาแล้ว ${numberWithCommas(publishResult.published_count)} ใบ` +
      (publishResult.unmatched_count > 0
        ? ` (ยังไม่จับคู่ร้าน ${numberWithCommas(publishResult.unmatched_count)} ใบ)`
        : "")
    : "";
  const skippedNoBranchCount = publishResult?.skipped_no_branch_count ?? 0;
  const pastSkippedCount = result.past_delivery_skipped_count ?? 0;
  // skipped_count ของ API รวมแถวเกินกำหนดส่งที่ไม่ได้นำเข้าไว้ด้วย จึงหักออกให้เหลือเฉพาะ
  // แถวซ้ำ/อ่านไม่ได้ แล้วแสดงแถวเกินกำหนดส่งแยกอีกบรรทัด
  const duplicateOrUnreadableCount = Math.max(
    result.skipped_count - pastSkippedCount,
    0,
  );

  const handleDownloadSkipped = () => {
    downloadUnreadableRowsCsv(
      skippedReport,
      `po-import-skipped-${result.batch_uuid.slice(0, 8)}.csv`,
    );
  };

  return (
    <Stack gap={2}>
      <Card sx={CARD_SX}>
        <CardContent sx={CARD_CONTENT_SX}>
          <Typography variant="subtitle1">
            บันทึกไฟล์ {result.file_name} แล้ว
          </Typography>
          <Typography variant="body2" color="text.secondary">
            เชน {PURCHASE_ORDER_CHAIN_LABEL[result.chain]}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                แถวในไฟล์
              </Typography>
              <Typography variant="h6">
                {numberWithCommas(result.row_count)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                บันทึกเป็น PO
              </Typography>
              <Typography variant="h6">
                {numberWithCommas(result.saved_count)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                ข้ามเพราะซ้ำหรืออ่านไม่ได้
              </Typography>
              <Typography variant="h6">
                {numberWithCommas(duplicateOrUnreadableCount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {PURCHASE_ORDER_STATUS_LABEL.UNMATCHED_CUSTOMER}
              </Typography>
              <Typography variant="h6">
                {numberWithCommas(result.unmatched_count)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL} (ไม่นำเข้า)
              </Typography>
              <Typography variant="h6">
                {numberWithCommas(pastSkippedCount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {PURCHASE_ORDER_STATUS_LABEL.NO_BRANCH}
              </Typography>
              <Typography
                variant="h6"
                color={hasNoBranch ? "secondary.main" : undefined}
              >
                {numberWithCommas(result.no_branch_count)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={CARD_SX}>
        <CardContent sx={CARD_CONTENT_SX}>
          <Stack gap={1.5}>
            {result.saved_count === 0 && (
              <Alert severity="info">
                ไฟล์นี้ไม่มีใบใหม่ที่ต้องส่ง ทุกแถวซ้ำกับ PO
                ที่มีอยู่แล้วหรืออ่านไม่ได้
              </Alert>
            )}

            {hasNoBranch && !publishResult && (
              <Alert severity="warning">
                มี {numberWithCommas(result.no_branch_count)} ใบยังไม่ระบุสาขา —
                ใบกลุ่มนี้จะยังไม่ถูกส่งให้สาขา ระบุสาขาต่อได้ที่ รายการ PO
              </Alert>
            )}

            {!hasNothingToPublish && result.unmatched_count > 0 && (
              <Alert severity="info">
                มี {numberWithCommas(result.unmatched_count)} ใบที่ยังไม่รู้ร้าน
                — สามารถส่งให้สาขาได้ตามปกติ
              </Alert>
            )}

            {publishResult && (
              <Stack gap={1}>
                <Alert severity="success">{publishedText}</Alert>

                {skippedNoBranchCount > 0 && (
                  <Alert severity="warning">
                    ยังไม่ส่ง {numberWithCommas(skippedNoBranchCount)}{" "}
                    ใบที่ยังไม่ระบุสาขา — ระบุสาขาที่ รายการ PO แล้วส่งให้สาขาจากจอนั้น
                  </Alert>
                )}

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>สาขา</TableCell>
                      <TableCell align="right">ส่งแล้ว</TableCell>
                      <TableCell align="right">
                        {PURCHASE_ORDER_STATUS_LABEL.UNMATCHED_CUSTOMER}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {publishResult.branches.map((summary) => (
                      <TableRow key={summary.branch.company_id}>
                        <TableCell>
                          {branchOptionLabel(summary.branch)}
                        </TableCell>
                        <TableCell align="right">
                          {numberWithCommas(summary.published_count)}
                        </TableCell>
                        <TableCell align="right">
                          {numberWithCommas(summary.unmatched_count)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            )}

            {!publishResult && (
              <Box>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handlePublish}
                  loading={publishing}
                  disabled={hasNothingToPublish}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  ส่งให้สาขาทั้ง {numberWithCommas(publishableCount)} ใบ
                </Button>
              </Box>
            )}

            <Stack direction="row" gap={1} flexWrap="wrap">
              {hasNoBranch && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<StoreIcon />}
                  component={RouterLink}
                  to={noBranchListLink}
                >
                  ไประบุสาขา {numberWithCommas(result.no_branch_count)} ใบ
                </Button>
              )}
              {skippedReport.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSkipped}
                >
                  แถวที่ข้าม ({numberWithCommas(skippedReport.length)}) CSV
                </Button>
              )}
              <Button variant="outlined" onClick={onRestart}>
                นำเข้าไฟล์ใหม่
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default DoneStep;
